import { Alert, Box, Button, useOpenClose } from '@palmetto/palmetto-components';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ChangeOrderIneligibleCode } from 'types/ChangeOrders';
import { UserPermissions } from 'types/UserTypes';

import { percentageFormatter } from '../../helpers/percentageFormatter';
import { useValidateChangeOrderQuery } from '../../services/changeOrders';
import { DiffErrorAlerProps, Origin } from '../../types/ProductionValueCheck';
import RequirePermissions from '../auth/requirePermissions';

import { GrantToleranceExceptionModal } from './GrantToleranceExceptionModal';
import { VoidConfirmationModal } from './VoidConfirmationModal';

export function DiffErrorAlert({
  errorObject,
  requestVoid,
  loadingState,
  handleToleranceExceptionModal,
  modalCTA2Text,
  easyChangeOrder,
  accountId,
  formData,
}: DiffErrorAlerProps) {
  const { isOpen, handleClose, handleOpen } = useOpenClose();
  const {
    isOpen: isOpenGrantToleranceModal,
    handleClose: handleCloseGrantToleranceModal,
    handleOpen: handleOpenGrantToleranceModal,
  } = useOpenClose();
  const location = useLocation();
  const navigate = useNavigate();
  const origin = location?.state?.from as Origin;
  const productionDifference = errorObject.production?.percentErrorDiff || 0;

  // Only when prompting for a change order: check the account can actually open one, so we never
  // route the user to a create form that would reject them. Skipped entirely when the flag is off.
  const { data: changeOrderEligibility } = useValidateChangeOrderQuery(
    { accountId: accountId ?? '' },
    { skip: !easyChangeOrder || !accountId },
  );

  // Route to the change-order flow with the just-entered as-built values prefilled, so the EPC
  // amends the contract instead of voiding it.
  const goToCreateChangeOrder = useCallback(() => {
    const params = new URLSearchParams();
    if (formData?.systemSizeKw) params.set('systemSizeKw', formData.systemSizeKw);
    if (formData?.systemFirstYearProductionKwh) {
      params.set('systemFirstYearProductionKwh', formData.systemFirstYearProductionKwh);
    }
    navigate(`/accounts/${accountId}/change-order/create?${params.toString()}`);
  }, [navigate, accountId, formData]);

  const loading =
    loadingState.isAccountLoading ||
    loadingState.isCurrentContractLoading ||
    loadingState.isCurrentDesignLoading ||
    loadingState.isChecking ||
    loadingState.isVoiding;

  return (
    <Box margin="0 0 xs 0" padding={{ base: '0 0 0 0', tablet: '0 0 xs 0' }}>
      <Alert
        data-testid="prod-val-check-diff-error-alert"
        message={
          <Box width={{ base: 'auto', tablet: '100' }}>
            <Box fontWeight={'bold'}>
              {percentageFormatter.format(productionDifference)} difference would require{' '}
              {easyChangeOrder ? 'a change order' : 'a new contract to by'} signed by the customer
            </Box>
            <Box direction={{ base: 'column', tablet: 'row' }}>
              <Box>
                {easyChangeOrder ? (
                  <Box padding={'sm 0 0 0'}>
                    {changeOrderEligibility?.eligible ? (
                      <>
                        <Box padding={'0 0 xs 0'}>
                          Create a change order to update the contracted values and send the amendment to the
                          customer for signature. The existing contract stays in place until the change order is
                          accepted.
                        </Box>
                        <Box padding={'sm 0 0 0'}>
                          <Button
                            variant="secondary"
                            tone="danger"
                            size={'sm'}
                            onClick={goToCreateChangeOrder}
                            style={{
                              maxWidth: 'fit-content',
                            }}
                            disabled={loading}
                            className="danger"
                          >
                            Create Change Order
                          </Button>
                        </Box>
                      </>
                    ) : changeOrderEligibility ? (
                      // Ineligible — explain why instead of dead-ending at a create form that would reject.
                      // Call out a pending change order specifically; otherwise surface the server's reason.
                      <Box padding={'0 0 xs 0'} data-testid="change-order-ineligible-message">
                        {changeOrderEligibility.ineligibleCode === ChangeOrderIneligibleCode.CHANGE_ORDER_PENDING
                          ? 'A change order is already pending for this account. It must be resolved (accepted or declined) before the production value can be updated.'
                          : (changeOrderEligibility.ineligibleReason ??
                            'A change order can’t be created for this account right now — contact LightReach support.')}
                      </Box>
                    ) : null}
                  </Box>
                ) : (
                  <Box padding={'sm 0 0 0'}>
                    <Box padding={'0 0 xs 0'}>To achieve install approval:</Box>
                    <Box
                      as="ol"
                      style={{
                        paddingLeft: '1.5em',
                        lineHeight: '1.4em',
                      }}
                    >
                      <li>Void the existing quote and contract</li>
                      <li>Update annual system production with the installed system production estimate</li>
                      <li>Create a new contract</li>
                      <li>Customer e-sign new contract</li>
                    </Box>
                    <Box padding={'sm 0 0 0'}>
                      <Button
                        variant="secondary"
                        tone="danger"
                        size={'sm'}
                        onClick={handleOpen}
                        style={{
                          maxWidth: 'fit-content',
                        }}
                        disabled={loading}
                        className="danger"
                      >
                        Void Contract and Update Year-1 Production Value
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
              {handleToleranceExceptionModal && origin !== Origin.PV_SYSTEM_TAB && (
                <RequirePermissions permissions={[UserPermissions.lightReachSupport]}>
                  <Box
                    width={{ base: 'auto', tablet: '5xl' }}
                    display={{ base: 'flex' }}
                    alignItems={{ base: 'flex-end' }}
                    justifyContent={{ base: 'flex-end' }}
                  >
                    <Button
                      variant="secondary"
                      size={'sm'}
                      onClick={handleOpenGrantToleranceModal}
                      tone="neutral"
                      style={{
                        maxWidth: 'fit-content',
                      }}
                      disabled={loading}
                    >
                      Grant Tolerance Exception
                    </Button>
                  </Box>
                </RequirePermissions>
              )}
            </Box>
          </Box>
        }
        variant="danger"
        hasIcon={true}
      />
      {!easyChangeOrder && (
        <VoidConfirmationModal
          isOpen={isOpen}
          isVoiding={loadingState.isVoiding}
          onVoidRequest={async () => {
            try {
              await requestVoid();
            } catch (e) {
              console.error({ e });
            }
            handleClose();
          }}
          onDismiss={() => {
            handleClose();
          }}
          cta2Text={modalCTA2Text}
        />
      )}
      {handleToleranceExceptionModal && (
        <GrantToleranceExceptionModal
          isOpen={isOpenGrantToleranceModal}
          onDismiss={() => {
            handleCloseGrantToleranceModal();
          }}
          handleToleranceExceptionModal={handleToleranceExceptionModal}
        />
      )}
    </Box>
  );
}
